/**
 * Which targets the map shows right now. One rule, used by the target markers
 * and by the RF operator markers / link layer, so an operator or dotted line
 * can never be drawn for a drone whose marker is hidden.
 *
 *   drones-ctx:           every target
 *   surv-ctx fused:       every target
 *   surv-ctx diagnostic:  targets whose source_services includes the selected
 *                         emitter — membership-based, no priority dedup, so an
 *                         aircraft seen by several sources shows under each
 *                         pill (ticket 86e1fvf06)
 *   any other ctx:        hidden
 */
import type { Target } from '@/lib/mqtt/schemas/targets';
import { useSurvStore } from '@/lib/store/surv';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';

export function visibleTargets(): Target[] {
  const state = useTargetsStore.getState();
  const ui = useUiStore.getState();
  const surv = useSurvStore.getState();
  const all = Object.values(state.byId);
  if (ui.activeContext === 'drones') return all;
  if (ui.activeContext !== 'surv') return [];
  if (surv.mode === 'fused') return all;
  if (surv.selectedNetwork === 'live_fused' && surv.fusedService) {
    // The fused feed carries the union of its inputs' traffic. No emitter
    // publishes `live_fused`, so `source_services` never contains it —
    // match against the fused inputs instead.
    const inputs = new Set(surv.fusedSelected);
    return all.filter((t) => t.source_services.some((src) => inputs.has(src)));
  }
  const svc = surv.selectedNetwork ? surv.byId[surv.selectedNetwork] : null;
  return svc ? all.filter((t) => t.source_services.includes(svc.service_id)) : [];
}
